import { useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import {
  Activity,
  ArrowDown,
  ArrowRight,
  Bot,
  Check,
  ChevronDown,
  Clock3,
  GitMerge,
  Layers3,
  Network,
  RefreshCw,
  Search,
  Sprout,
  AlertTriangle,
} from 'lucide-react'
import { useGardenOperations } from '../hooks/useKnowledge'
import type {
  GardenOperations,
  PipelineStage,
  ProposalAction,
  ResolutionExample,
} from '../api/knowledgeApi'
import { exampleChange, exampleState } from './resolutionExample'

const labels: Record<ProposalAction, string> = {
  merge: 'Merges',
  split: 'Splits',
  category: 'Categories',
  generic: 'Weights',
  reject: 'Removals',
  link: 'Relations',
}
const statusText: Record<string, string> = {
  succeeded: 'Succeeded',
  failed: 'Needs attention',
  running: 'Running',
  stale: 'No recent heartbeat',
  unknown: 'Not verified',
  paused: 'Paused',
  provider_blocked: 'Provider blocked',
  calibration_failed: 'Calibration held back',
  budget_exhausted: 'Run budget reached',
  ok: 'Complete',
  queued: 'Queued',
  waiting: 'Awaiting build',
  idle: 'Enabled · idle',
  available: 'Imported snapshot',
  done: 'Complete',
}
const relative = (date?: string | null) =>
  date && Number.isFinite(Date.parse(date))
    ? formatDistanceToNow(new Date(date), { addSuffix: true })
    : 'not recorded'
const num = (n?: number | null) => (n == null ? '—' : n.toLocaleString())
function Status({ state }: { state: string }) {
  const bad = ['failed', 'stale', 'provider_blocked', 'calibration_failed'].includes(state)
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] ${bad ? 'text-amber-800' : ['waiting', 'paused'].includes(state) ? 'text-amber-700' : state === 'unknown' ? 'text-slate-500' : 'text-emerald-700'}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full bg-current ${state === 'running' ? 'motion-safe:animate-pulse' : ''}`}
      />
      {statusText[state] || state.replace(/_/g, ' ')}
    </span>
  )
}
function Example({ example }: { example: ResolutionExample }) {
  const [open, setOpen] = useState(false)
  const state = exampleState(example)
  const change = exampleChange(example)
  return (
    <article className="overflow-hidden rounded-xl border bg-card transition-colors hover:border-arthaus-gold/50">
      <button
        className="w-full p-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-arthaus-gold"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="flex items-center justify-between gap-2 text-[10px]">
          <span className="uppercase tracking-[.15em] text-muted-foreground">
            {labels[example.action]} ·{' '}
            {example.origin === 'autopilot'
              ? 'Autopilot'
              : example.origin === 'manual'
                ? 'Bulk accept'
                : 'Person'}
          </span>
          <ChevronDown
            className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </span>
        {['category', 'generic'].includes(example.action) && (
          <span className="mt-3 block font-serif text-lg">
            {example.decisionPayload?.conceptName ||
              example.conceptNames.filter(Boolean).join(' + ')}
          </span>
        )}
        <span className="mt-3 block font-serif text-base leading-relaxed">{change.before}</span>
        <span className="my-2 flex items-center gap-2 text-arthaus-gold">
          <ArrowDown className="h-3.5 w-3.5" />
          <span className="text-[10px]">
            {Math.round(example.confidence * 100)}% model confidence
          </span>
        </span>
        <span className="block font-serif text-lg leading-snug">{change.after}</span>
        <span
          className={`mt-4 inline-flex rounded-full px-2 py-1 text-[10px] ${['local', 'applied'].includes(state.key) ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'bg-arthaus-gold/10 text-amber-800 dark:text-amber-300'}`}
        >
          {state.label}
        </span>
        <span className="ml-2 text-[10px] text-muted-foreground">
          {relative(example.decidedAt)}
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t bg-muted/25 p-4 text-sm">
          <p className="leading-relaxed text-muted-foreground">
            {example.reasoning || 'No model explanation recorded.'}
          </p>
          {state.key === 'local' && (
            <p className="text-xs text-muted-foreground">
              This edit took effect in AMS when accepted. Search propagation is tracked separately
              through export and the next build.
            </p>
          )}
          {state.key === 'waiting' && (
            <p className="text-xs text-muted-foreground">
              Acceptance records the instruction. The concepts remain separate until a build applies
              the rule and AMS imports it.
            </p>
          )}
          {example.appliedRunId && (
            <p className="break-all text-xs text-muted-foreground">
              Applied in {example.appliedRunId}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {example.ucids.map((id, i) => (
              <Link
                className="rounded-full border bg-background px-2.5 py-1 text-xs hover:border-arthaus-gold"
                key={id}
                to={`/app/knowledge/${id}`}
              >
                {example.decisionPayload?.conceptNames?.[i] ||
                  example.conceptNames[i] ||
                  'Open concept'}{' '}
                ↗
              </Link>
            ))}
          </div>
          {example.artworkIds?.length > 0 && (
            <div className="flex gap-2">
              {example.artworkIds.map((id) => (
                <Link
                  key={id}
                  to={`/app/artworks/${id}`}
                  className="h-20 w-16 overflow-hidden rounded-md border bg-background p-1"
                >
                  <img
                    className="h-full w-full object-contain"
                    src={`/artwork-ms/api/internal/artworks/${id}/preview`}
                    alt={`Artwork ${id} carrying this concept`}
                    loading="lazy"
                  />
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </article>
  )
}

function Pipeline({ data }: { data: GardenOperations }) {
  const [selected, setSelected] = useState('build')
  const openJobs = data.jobs.filter((j) => j.status === 'running' || j.status === 'queued')
  const on = data.policies.filter((p) => p.enabled).length
  const stages: Array<{
    id: string
    title: string
    detail: string
    explanation: string
    outcome: string
    icon: typeof Network
    status: PipelineStage
    href: string
  }> = [
    {
      id: 'build',
      title: 'Build',
      detail: 'Bring artwork descriptions into a shared vocabulary.',
      explanation:
        'The graph job brings source concepts together and applies accepted structural rules. The latest imported build tells us what has actually reached AMS.',
      outcome: data.lastBuild
        ? `${num(data.lastBuild.newConcepts)} new · ${num(data.lastBuild.mergedConcepts)} merged in last import`
        : 'No imported build recorded',
      icon: Layers3,
      status: data.pipeline.build,
      href: '/app/knowledge/changes',
    },
    {
      id: 'resolve',
      title: 'Resolve',
      detail: 'Review concepts and propose clearer relationships.',
      explanation:
        'The resolution agent reviews semantic domains and their boundaries, checks its calibration, and records proposed changes for the decision stage.',
      outcome: data.resolution
        ? `${num(data.resolution.verdicts)} reviewed · ${num(data.resolution.proposals)} proposed`
        : 'No resolution run recorded',
      icon: Bot,
      status: { state: data.resolution?.observedState || 'unknown' },
      href: '/app/knowledge/agent',
    },
    {
      id: 'decide',
      title: 'Decide',
      detail: 'Review proposals or accept them under enabled policies.',
      explanation:
        'People review proposals, while enabled autopilot policies accept eligible actions at their configured thresholds. Splits and relationships require a person.',
      outcome: `${num(data.totals.pending)} pending · ${on}/${data.policies.length} policies on`,
      icon: Check,
      status: { state: openJobs.length ? 'running' : on ? 'idle' : 'paused' },
      href: '/app/knowledge/proposals',
    },
    {
      id: 'apply',
      title: 'Apply rules',
      detail: 'Carry accepted structural changes into the next build.',
      explanation:
        'Accepted structural rules are exported for a graph build to apply. Acceptance and export are separate from confirmed application. Categories and weights take effect in AMS when accepted.',
      outcome: `${num(data.totals.applications.applied)} applied · ${num(data.totals.applications.waiting)} waiting`,
      icon: GitMerge,
      status: data.totals.applications.needsAttention
        ? { state: 'failed' }
        : data.totals.applications.waiting
          ? { state: 'waiting' }
          : data.pipeline.export,
      href: '/app/knowledge/rules',
    },
    {
      id: 'sync',
      title: 'Import',
      detail: 'Bring the rebuilt graph into the Knowledge section.',
      explanation:
        'The import copies a completed graph into AMS for browsing and review. Proposal pulls happen separately; a successful scheduler attempt alone does not establish a fresh graph import.',
      outcome: data.lastBuild
        ? `Last import ${relative(data.lastBuild.syncedAt)}`
        : 'No successful import recorded',
      icon: RefreshCw,
      status: data.pipeline.sync,
      href: '/app/knowledge/progress',
    },
    {
      id: 'explore',
      title: 'Explore',
      detail: 'Explore the ideas, artworks and connections now available.',
      explanation:
        'Explore the last imported graph through concept pages, the shared-domain agenda, and the 2D or immersive 3D explorer. This is the browsing snapshot, not confirmation that every accepted rule is applied.',
      outcome: `${num(data.lastBuild?.concepts)} concepts · ${num(data.lastBuild?.artworkLinks)} artwork links`,
      icon: Search,
      status: { state: data.lastBuild ? 'available' : 'unknown' },
      href: '/app/knowledge/graph',
    },
  ]
  const active = stages.find((s) => s.id === selected)!
  return (
    <div className="overflow-hidden rounded-2xl border border-arthaus-gold/20 bg-gradient-to-br from-white to-amber-50/40 text-slate-900 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3 px-6 pt-6">
        <div>
          <p className="text-[9px] uppercase tracking-[.3em] text-arthaus-gold">
            How the garden works
          </p>
          <h2 className="mt-2 font-serif text-3xl">Watch knowledge take shape.</h2>
          <p className="mt-2 max-w-xl text-xs leading-relaxed text-slate-600">
            From artwork descriptions to a graph you can explore. Follow the phases below; select
            one for its latest outcomes, what is waiting, and the next step.
          </p>
        </div>
        <span className="flex items-center gap-1.5 text-[10px] text-slate-500">
          <Clock3 className="h-3 w-3" />
          Observed {relative(data.observedAt)}
        </span>
      </div>
      <div className="overflow-x-auto px-6 pt-6">
        <div className="relative min-w-[850px] pb-8">
          <div
            aria-hidden="true"
            className="absolute left-8 right-8 top-12 border-t border-dashed border-arthaus-gold/30"
          />
          <div className="relative grid grid-cols-6 gap-3">
            {stages.map(({ id, title, detail, outcome, icon: Icon, status }, i) => (
              <button
                key={id}
                onClick={() => setSelected(id)}
                aria-pressed={selected === id}
                className={`group min-h-36 rounded-xl border p-3 text-left transition duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-arthaus-gold ${selected === id ? 'border-arthaus-gold/70 bg-amber-50 shadow-sm' : 'border-slate-200 bg-white hover:-translate-y-1 hover:border-arthaus-gold/40 motion-reduce:transform-none'}`}
              >
                <span className="flex items-center justify-between">
                  <Icon className="h-5 w-5 text-arthaus-gold" />
                  <span className="text-[9px] text-slate-400">0{i + 1}</span>
                </span>
                <span className="mt-4 block font-serif text-lg">{title}</span>
                <span className="mt-1 block min-h-8 text-[10px] leading-relaxed text-slate-500">
                  {detail}
                </span>
                <span className="mb-3 mt-3 block text-xs font-medium leading-relaxed text-slate-800">
                  {outcome}
                </span>
                <Status state={status.state} />
              </button>
            ))}
          </div>
          <div className="absolute bottom-1 left-[9%] right-[25%] flex items-center gap-2 text-[9px] text-slate-500">
            <span className="h-3 w-3 border-b border-l border-arthaus-gold/30" />
            <span className="flex-1 border-t border-arthaus-gold/20" />
            Structural decisions feed the next build
            <span className="flex-1 border-t border-arthaus-gold/20" />
            <span>↶</span>
          </div>
        </div>
      </div>
      <div
        className="grid gap-4 border-t border-arthaus-gold/15 bg-white/80 px-6 py-5 md:grid-cols-[1fr_auto]"
        aria-live="polite"
      >
        <div>
          <div className="flex items-center gap-3">
            <h3 className="font-serif text-xl">{active.title}</h3>
            <Status state={active.status.state} />
          </div>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
            {active.explanation}
          </p>
          <p className="mb-1 mt-4 text-[10px] font-medium uppercase tracking-widest text-slate-500">
            Latest observation
          </p>
          {selected === 'build' && (
            <div className="mt-2 text-xs leading-relaxed text-slate-600">
              <p>Last successful AMS import {relative(data.lastBuild?.syncedAt)} ·{' '}
              {num(data.lastBuild?.newConcepts)} new concepts ·{' '}
              {num(data.lastBuild?.mergedConcepts)} merged.</p>
              <details className="mt-2"><summary className="cursor-pointer underline underline-offset-2">Latest graph job observation</summary><p className="mt-2 break-words">{data.pipeline.build.execution || 'No execution identifier recorded'}. {data.pipeline.build.message || 'No execution details available.'}</p></details>
            </div>
          )}
          {selected === 'resolve' && (
            <p className="mt-2 text-xs leading-relaxed text-slate-600">
              {num(data.resolution?.verdicts)} verdicts · {num(data.resolution?.proposals)}{' '}
              proposals in the latest run. Last heartbeat {relative(data.resolution?.finishedAt)}.
              <br />
              {data.resolution?.observedState === 'stale'
                ? 'The stored running flag has no recent heartbeat. It does not establish that a worker is still active.'
                : data.resolution
                  ? `Reviewed by ${data.resolution.model}. Open resolution details for calibration, spend and individual verdicts.`
                  : 'No resolution run has been recorded yet.'}
            </p>
          )}
          {selected === 'decide' && (
            <div className="mt-2 text-xs leading-relaxed text-slate-600">
              <p>
                {num(data.totals.pending)} proposals waiting. Splits and relations always require a
                person.
              </p>
              <p>
                {data.policies
                  .map(
                    (p) =>
                      `${labels[p.action]} ≥ ${p.minConfidence.toFixed(2)} ${p.enabled ? 'on' : 'off'}`
                  )
                  .join(' · ')}
              </p>
              {data.jobs.slice(0, 3).map((j) => (
                <p key={j.id}>
                  {j.origin === 'autopilot' ? 'Autopilot' : 'Bulk accept'} · {labels[j.action]}:{' '}
                  {j.accepted}/{j.total} · {j.status}
                  {j.error ? ` · ${j.error}` : ''}
                </p>
              ))}
            </div>
          )}
          {selected === 'apply' && (
            <p className="mt-2 text-xs leading-relaxed text-slate-600">
              {num(data.totals.applications.applied)} structural rules confirmed applied ·{' '}
              {num(data.totals.applications.waiting)} awaiting application ·{' '}
              {num(data.totals.applications.needsAttention)} need attention.
              <br />
              Rule-export scheduler:{' '}
              {statusText[data.pipeline.export.state] || data.pipeline.export.state}. Categories and
              weights take effect in AMS at acceptance; search propagation follows the build.
            </p>
          )}
          {selected === 'sync' && (
            <p className="mt-2 text-xs leading-relaxed text-slate-600">
              Last scheduler attempt {relative(data.pipeline.sync.lastAttemptAt)}
              {data.pipeline.sync.errorCode ? ` · error ${data.pipeline.sync.errorCode}` : ''}. Last
              successful graph import {relative(data.lastBuild?.syncedAt)}.<br />
              Hourly proposal pull {relative(data.lastProposalPullAt)} · scheduler{' '}
              {statusText[data.pipeline.proposals.state] || data.pipeline.proposals.state}.{' '}
              {data.pipeline.sync.message}
            </p>
          )}
          {selected === 'explore' && (
            <p className="mt-2 text-xs leading-relaxed text-slate-600">
              Browse the imported graph in 2D or immersive 3D. {num(data.lastBuild?.artworkLinks)}{' '}
              artwork links and {num(data.lastBuild?.edges)} concept connections.
              <br />
              This view reflects the last imported build, with AMS curation edits. It does not imply
              that every accepted structural rule is already applied.
            </p>
          )}
        </div>
        <Link
          className="inline-flex h-fit items-center gap-2 self-center rounded-full border border-slate-200 bg-white px-4 py-2 text-xs transition hover:border-arthaus-gold"
          to={active.href}
        >
          Open {active.title.toLowerCase()} details <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <p className="border-t border-slate-100 px-6 py-3 text-[11px] leading-relaxed text-slate-500">
        Each phase shows its latest observation; these may come from different runs. Accepted →
        exported → applied → imported are separate milestones.
      </p>
    </div>
  )
}

export function GardenDashboard({ view = 'full' }: { view?: 'full' | 'pipeline' | 'preview' }) {
  const [days, setDays] = useState(7)
  const [action, setAction] = useState('all')
  const [state, setState] = useState('all')
  const [day, setDay] = useState<string | null>(null)
  const [shown, setShown] = useState(6)
  const { data, isLoading, isError, isFetching, refetch } = useGardenOperations(days)
  if (isLoading)
    return (
      <div
        className="h-72 animate-pulse rounded-2xl bg-muted motion-reduce:animate-none"
        aria-label="Loading knowledge operations"
      />
    )
  if (isError || !data)
    return (
      <div
        role="alert"
        className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-400/30 bg-amber-500/5 p-5 text-sm"
      >
        <span>
          Knowledge operations could not load. Use the concept catalogue to continue exploring.
        </span>
        <button onClick={() => refetch()} className="rounded-full border px-3 py-1">
          Retry operations
        </button>
      </div>
    )
  if (view === 'pipeline')
    return (
      <section aria-label="Knowledge pipeline">
        <Pipeline data={data} />
      </section>
    )
  if (view === 'preview')
    return (
      <section
        aria-label="Recent resolutions"
        className="space-y-4 rounded-2xl border bg-card p-5 md:p-6"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-serif text-2xl">Recent resolutions</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              A glimpse of the decisions shaping the garden. Open an example for its reasoning.
            </p>
          </div>
          <Link
            to="/app/knowledge/operations"
            className="inline-flex items-center gap-2 text-sm underline underline-offset-4"
          >
            View operations & all resolutions <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        {data.examples.length ? (
          <div className="grid items-start gap-3 md:grid-cols-2">
            {data.examples.slice(0, 2).map((e) => (
              <Example key={e.proposalId} example={e} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No accepted resolutions in the current sample.
          </p>
        )}
      </section>
    )
  const examples = data.examples.filter(
    (e) =>
      (action === 'all' || e.action === action) &&
      (state === 'all' ||
        (state === 'applied'
          ? ['local', 'applied'].includes(exampleState(e).key)
          : exampleState(e).key === state)) &&
      (!day ||
        new Intl.DateTimeFormat('en-CA', { timeZone: data.timeZone }).format(
          new Date(e.decidedAt)
        ) === day)
  )
  const max = Math.max(1, ...data.daily.map((d) => Math.max(d.proposed, d.autopilot + d.person)))
  return (
    <section className="space-y-5" aria-label="Knowledge garden operations">
      <Pipeline data={data} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl">A little more understood, every day.</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Decisions during the selected window. Applied structural rules are reported separately.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            aria-label="Progress window"
            className="rounded-full border bg-background px-3 py-2 text-xs"
            value={days}
            onChange={(e) => {
              setDays(Number(e.target.value))
              setDay(null)
            }}
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          <button
            aria-label="Refresh operations"
            onClick={() => refetch()}
            className="rounded-full border p-2"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin motion-reduce:animate-none' : ''}`}
            />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          {
            label: 'Accepted',
            value: data.totals.acceptedWindow,
            hint: 'decisions recorded',
            icon: Check,
          },
          {
            label: 'By autopilot',
            value: data.totals.autopilotWindow,
            hint: 'included in accepted',
            icon: Bot,
          },
          {
            label: 'Awaiting a build',
            value: data.totals.applications.waiting,
            hint: 'structural rules, all time',
            icon: GitMerge,
          },
          {
            label: 'Proposals waiting',
            value: data.totals.pending,
            hint: 'current backlog',
            icon: Sprout,
          },
        ].map(({ label, value, hint, icon: Icon }) => (
          <div key={label} className="rounded-xl border bg-card p-4">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              {label}
              <Icon className="h-4 w-4 text-arthaus-gold" />
            </div>
            <p className="mt-3 font-serif text-3xl tabular-nums">{num(value)}</p>
            <p className="mt-1 text-[10px] text-muted-foreground">{hint}</p>
          </div>
        ))}
      </div>
      <div className="rounded-xl border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-medium">
            <Activity className="h-4 w-4 text-arthaus-gold" /> Proposed and accepted
          </h3>
          <span className="text-[10px] text-muted-foreground">
            Gold: proposed · green: accepted · dark green: autopilot · Central time
          </span>
        </div>
        {data.daily.length ? (
          <div className="mt-5 flex h-32 items-end gap-2 overflow-x-auto pb-5">
            {data.daily.map((d) => (
              <button
                key={d.day}
                aria-pressed={day === d.day}
                aria-label={`${d.day}: ${d.proposed} proposed, ${d.autopilot + d.person} accepted, ${d.autopilot} by autopilot`}
                title={`${d.day}: ${d.proposed} proposed · ${d.autopilot + d.person} accepted (${d.autopilot} autopilot)`}
                onClick={() => setDay(day === d.day ? null : d.day)}
                className={`relative flex h-full min-w-8 flex-1 items-end justify-center gap-1 rounded-t transition ${day === d.day ? 'bg-arthaus-gold/10' : 'hover:bg-muted'}`}
              >
                <span
                  className="w-3 rounded-t bg-arthaus-gold/80 transition-all duration-500 motion-reduce:transition-none"
                  style={{ height: `${(100 * d.proposed) / max}%` }}
                />
                <span
                  className="flex w-3 flex-col justify-end overflow-hidden rounded-t bg-emerald-400/70 transition-all duration-500 motion-reduce:transition-none"
                  style={{ height: `${(100 * (d.autopilot + d.person)) / max}%` }}
                >
                  <span
                    className="w-full bg-emerald-800"
                    style={{
                      height: `${(100 * d.autopilot) / Math.max(1, d.autopilot + d.person)}%`,
                    }}
                  />
                </span>
                <span className="absolute -bottom-5 text-[9px] text-muted-foreground">
                  {d.day.slice(5)}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <p className="mt-4 text-sm text-muted-foreground">
            No proposal or acceptance events in this window.
          </p>
        )}
        <p className="mt-4 text-[10px] text-muted-foreground">
          Select a day to filter the resolution examples. Acceptance is not a measurement of
          independent model accuracy.
        </p>
      </div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl">See what changed.</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Real accepted resolutions, sampled across actions. Open one for the reasoning,
            application state and affected concepts.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            aria-label="Resolution action"
            className="rounded-full border bg-background px-3 py-2 text-xs"
            value={action}
            onChange={(e) => {
              setAction(e.target.value)
              setShown(6)
            }}
          >
            <option value="all">All actions</option>
            {Object.entries(labels).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
          <select
            aria-label="Application state"
            className="rounded-full border bg-background px-3 py-2 text-xs"
            value={state}
            onChange={(e) => {
              setState(e.target.value)
              setShown(6)
            }}
          >
            <option value="all">All application states</option>
            <option value="waiting">Awaiting build</option>
            <option value="applied">Applied</option>
            <option value="attention">Needs attention</option>
            <option value="revoked">Revoked</option>
          </select>
          {day && (
            <button className="rounded-full border px-3 py-2 text-xs" onClick={() => setDay(null)}>
              Clear {day} ×
            </button>
          )}
        </div>
      </div>
      {examples.length ? (
        <div className="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3">
          {examples.slice(0, shown).map((e) => (
            <Example key={e.proposalId} example={e} />
          ))}
        </div>
      ) : (
        <p className="rounded-xl border p-5 text-sm text-muted-foreground">
          No examples in this sample match these filters.{' '}
          <Link className="underline" to="/app/knowledge/proposals">
            Open all proposals
          </Link>
          .
        </p>
      )}
      {examples.length > shown && (
        <button
          className="rounded-full border px-4 py-2 text-xs"
          onClick={() => setShown(shown + 6)}
        >
          Show more examples
        </button>
      )}
      <p className="flex items-start gap-2 text-[10px] leading-relaxed text-muted-foreground">
        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />A running flag without a heartbeat for
        45 minutes is shown as stale. Cloud status is cached for one minute; unavailable checks are
        marked unverified. This dashboard does not start jobs or accept proposals.
      </p>
    </section>
  )
}
